// Recent-orders persistence for the no-backend reorder surfaces.
//
// We keep up to 3 of the customer's most recent orders (most-recent-first,
// de-duplicated by content) so the hero "Reorder last delivery" drawer can
// offer a choice between their standard loads — not just the single latest
// order.
//
// IMPORTANT: this uses the `SavedOrder` shape (the reorder payload written
// by the cart), which is DIFFERENT from the `TrackedOrder` shape in
// utils/orderTracking.ts. They are not interchangeable.
//
// The legacy single-order key ('253gravel_last_order') is left untouched —
// other surfaces and older devices still read it. pushRecentOrder is purely
// additive, and getRecentOrders seeds from the legacy key on first read so
// existing customers immediately see their last order in the new drawer.

export type SavedOrderItem = {
  id?: string
  category: string
  product: string
  quantity: number
  pricePerYard: number
}

export type SavedOrder = {
  items: SavedOrderItem[]
  addressInput?: string
  name?: string
  phone?: string
  email?: string
}

const RECENT_ORDERS_KEY = '253gravel_recent_orders'
const LEGACY_LAST_ORDER_KEY = '253gravel_last_order'
// Tracked-order history (TrackedOrder shape, keyed by orderId). Used as a
// backfill so orders placed before this feature shipped — or only ever saved
// for tracking — still appear in the reorder drawer.
const TRACKED_ORDERS_KEY = '253gravel_tracked_orders'
const MAX_RECENT = 3

// Stable content signature used to de-duplicate. Two orders are "the same"
// if they request the same materials/quantities to the same address.
// NOTE: category is intentionally excluded — backfilled tracked orders don't
// carry a category, so keying on product+quantity+address lets the same order
// de-dupe correctly across the recent-orders array and the tracked history.
function normalizeAddress(addr?: string): string {
  return (addr || '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '')
    .slice(0, 40)
}
function orderSignature(order: SavedOrder): string {
  try {
    const items = (order.items || [])
      .map((i) => `${i.product}|${i.quantity}`)
      .sort()
      .join('~')
    return `${items}@@${normalizeAddress(order.addressInput)}`
  } catch {
    return Math.random().toString(36)
  }
}

function isValidOrder(o: unknown): o is SavedOrder {
  return (
    !!o &&
    typeof o === 'object' &&
    Array.isArray((o as SavedOrder).items) &&
    (o as SavedOrder).items.length > 0
  )
}

function readLegacyLastOrder(): SavedOrder | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = localStorage.getItem(LEGACY_LAST_ORDER_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    return isValidOrder(parsed) ? parsed : null
  } catch {
    return null
  }
}

function writeRecent(orders: SavedOrder[]) {
  if (typeof window === 'undefined') return
  try {
    localStorage.setItem(
      RECENT_ORDERS_KEY,
      JSON.stringify(orders.slice(0, MAX_RECENT)),
    )
  } catch {
    /* private window / quota — ignore */
  }
}

// Returns up to 3 recent orders, most-recent-first.
//
// Sources, merged in priority order and de-duped by content signature:
//   1. The recent-orders array (authoritative — carries real categories).
//   2. The legacy single-order key (in case the array is empty).
//
// IMPORTANT: the tracked-order history (`253gravel_tracked_orders`) is
// DELIBERATELY NOT a source here. That key is written by
// `saveOrderForTracking()` whenever ANY tracking link is opened — including
// a link from someone else's confirmation email/SMS or a shared link to an
// order that was never placed on this device. Backfilling it into the
// reorder drawer conflated "orders I'm watching via a link" with "orders I
// placed here", which surfaced other people's deliveries in this device's
// order history. Recent/reorder history now only reflects orders actually
// PLACED on this device (which the cart writes to the two keys above).
export function getRecentOrders(): SavedOrder[] {
  if (typeof window === 'undefined') return []
  const merged: SavedOrder[] = []
  const seen = new Set<string>()
  const add = (o: SavedOrder | null) => {
    if (!isValidOrder(o)) return
    const sig = orderSignature(o)
    if (seen.has(sig)) return
    seen.add(sig)
    merged.push(o)
  }
  // 1. Authoritative recent-orders array.
  try {
    const raw = localStorage.getItem(RECENT_ORDERS_KEY)
    if (raw) {
      const parsed = JSON.parse(raw)
      if (Array.isArray(parsed)) parsed.filter(isValidOrder).forEach(add)
    }
  } catch {
    /* ignore */
  }
  // 2. Legacy single-order key.
  add(readLegacyLastOrder())
  return merged.slice(0, MAX_RECENT)
}

// Prepend a freshly placed order to the recent list: de-dupe by content,
// cap at 3, most-recent-first. Additive — does not touch the legacy key.
export function pushRecentOrder(order: SavedOrder): void {
  if (typeof window === 'undefined' || !isValidOrder(order)) return
  const existing = getRecentOrders()
  const sig = orderSignature(order)
  const deduped = existing.filter((o) => orderSignature(o) !== sig)
  writeRecent([order, ...deduped])
}

// Clears all saved order history used by the reorder surfaces: the
// recent-orders array, the legacy single-order key, and the tracked-order
// history. After this the hero reverts to the default marketing greeting
// (first-time state) since no saved order remains.
export function clearRecentOrders(): void {
  if (typeof window === 'undefined') return
  try {
    localStorage.removeItem(RECENT_ORDERS_KEY)
    localStorage.removeItem(LEGACY_LAST_ORDER_KEY)
    localStorage.removeItem(TRACKED_ORDERS_KEY)
    localStorage.removeItem('253gravel_recent_addresses')
    localStorage.removeItem('253gravel_latest_order_id')
    localStorage.removeItem('reorder_chip_impressions_v1')
  } catch {
    /* ignore */
  }
}

// --- Recent delivery addresses ----------------------------------------
//
// A standalone, lightweight history of delivery addresses the customer has
// actually used/confirmed (in the Hero estimator, the Delivery Zones map, or
// at cart checkout). This is SEPARATE from the order history above: an
// address gets remembered the moment the customer confirms a location, even
// if they never finish placing an order. It powers the "Recent addresses"
// dropdown that appears when the empty address box is focused — our in-app
// replacement for the browser's native autofill popup.
const RECENT_ADDRESSES_KEY = '253gravel_recent_addresses'
const MAX_RECENT_ADDRESSES = 5

// Returns up to 5 recent addresses, most-recent-first. Seeds from prior
// orders so existing customers immediately see their last delivery address
// even before this standalone history has been written.
export function getRecentAddresses(): string[] {
  if (typeof window === 'undefined') return []
  const out: string[] = []
  const seen = new Set<string>()
  const add = (addr?: string) => {
    const a = (addr || '').trim()
    if (!a) return
    const key = a.toLowerCase()
    if (seen.has(key)) return
    seen.add(key)
    out.push(a)
  }
  // 1. Standalone recent-addresses list (authoritative).
  try {
    const raw = localStorage.getItem(RECENT_ADDRESSES_KEY)
    if (raw) {
      const parsed = JSON.parse(raw)
      if (Array.isArray(parsed)) parsed.forEach((a) => add(String(a)))
    }
  } catch {
    /* ignore */
  }
  // 2. Backfill from prior orders so the list isn't empty for returning
  //    customers who placed an order before this history existed.
  try {
    getRecentOrders().forEach((o) => add(o.addressInput))
  } catch {
    /* ignore */
  }
  return out.slice(0, MAX_RECENT_ADDRESSES)
}

// Remember an address the customer confirmed. De-dupes case-insensitively,
// most-recent-first, capped at 5. Safe to call on every confirmed address.
export function pushRecentAddress(address: string): void {
  if (typeof window === 'undefined') return
  const a = (address || '').trim()
  if (a.length < 4) return
  try {
    const existing = getRecentAddresses()
    const deduped = existing.filter((x) => x.toLowerCase() !== a.toLowerCase())
    localStorage.setItem(
      RECENT_ADDRESSES_KEY,
      JSON.stringify([a, ...deduped].slice(0, MAX_RECENT_ADDRESSES)),
    )
  } catch {
    /* private window / quota — ignore */
  }
}

// Shared display helper so every reorder surface labels an order identically.
export function summarizeOrder(order: SavedOrder): string {
  const items = order.items || []
  if (items.length === 0) return 'Saved order'
  if (items.length > 1) {
    const totalYards = items.reduce((s, i) => s + i.quantity, 0)
    return `${totalYards} yds · ${items.length} materials`
  }
  return `${items[0].quantity} yds ${items[0].product}`
}
